import { ArrowRight, BookOpen, Brain, CircleCheck, Compass, Flame, FlaskConical, Gauge, Layers, RotateCcw, Star, Sun, Timer, Trophy } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { EXAMS } from "../data/exams";
import { ACHIEVEMENTS } from "../lib/achievements";
import { LABS } from "../data/labs";
import { lessonsFor } from "../data/localized";
import { bankStats } from "../data/questions";
import { useAuth } from "../lib/auth";
import { ExamBadge, ProgressBar, useDocumentTitle } from "../components/ui";
import { dayKey } from "../lib/dates";
import { levelFromXp, liveStreak } from "../lib/progress";
import { buildDaily, displayTitle, dueQuestionIds, sessionStore, useSession } from "../lib/session";
import { latestDiagnostic } from "../lib/diagnostic";
import { examMastery, readiness } from "../lib/stats";
import { useProgress } from "../lib/store";
import { isMastered } from "../lib/srs";
import { pick, useT } from "../i18n";

export function HomePage() {
  useDocumentTitle("");
  const t = useT();
  const p = useProgress();
  const lang = p.lang;
  const active = useSession();
  const navigate = useNavigate();
  const today = dayKey();
  const daily = p.dailyDone[today];
  const due = dueQuestionIds(p, Date.now()).length;
  const mastered = Object.values(p.questions).filter((s) => isMastered(s.srs)).length;
  const lvl = levelFromXp(p.xp);
  const lessons = lessonsFor(lang);
  const nextLesson = lessons.find((l) => !p.lessons[l.id]);
  const isNew = Object.keys(p.questions).length === 0 && Object.keys(p.lessons).length === 0;
  const diag = latestDiagnostic(p);

  const authed = useAuth().status === "authed";
  const startDaily = () => {
    if (!authed) { navigate("/login?next=/"); return; }
    sessionStore.set(buildDaily(today));
    navigate("/session");
  };

  return (
    <>
      {active && (
        <div className="callout warn row between">
          <span>
            {t("Bạn đang làm dở:", "In progress:")} <strong>{displayTitle(active, lang)}</strong>{" "}
            ({Object.keys(active.answers).length}/{active.questionIds.length} {t("câu", "questions")})
          </span>
          <Link to="/session" className="btn sm primary">{t("Tiếp tục", "Continue")}</Link>
        </div>
      )}

      <section className="hero">
        <div className="card hero-main">
          <span className="chip accent">{t("Luyện thi chứng chỉ Claude · Song ngữ Việt–Anh", "Claude certification prep · Vietnamese–English")}</span>
          <h1 className="mt">{isNew ? t("Làm chủ Claude — từ người mới tới Architect", "Master Claude — from beginner to Architect") : t("Chào mừng trở lại!", "Welcome back!")}</h1>
          <p className="muted">
            {t(
              `Lộ trình ${lessons.length} bài học, ${bankStats().total} câu hỏi tình huống, ${LABS.length} lab thực hành chấm tự động và đề thi thử bám blueprint chính thức của 4 chứng chỉ Claude. Đề thi bằng tiếng Anh như kỳ thi thật.`,
              `A ${lessons.length}-lesson path, ${bankStats().total} scenario questions, ${LABS.length} auto-graded labs and mock exams that follow the official blueprints of all 4 Claude certifications. Questions are in English, just like the real exam.`,
            )}
          </p>
          <div className="row mt">
            {nextLesson ? (
              <Link className="btn primary lg" to={`/learn/${nextLesson.id}`}>
                {isNew ? t("Bắt đầu học từ đầu", "Start from the beginning") : `${t("Học tiếp", "Continue")}: ${nextLesson.title}`}
              </Link>
            ) : (
              <Link className="btn primary lg" to="/exams">{t("Thi thử ngay", "Take a mock exam")}</Link>
            )}
            <button type="button" className="btn lg" onClick={startDaily} disabled={!!daily}>
              {daily
                ? <><CircleCheck className="ico" size={18} aria-hidden="true" /> {t("Thử thách hôm nay", "Today's challenge")}: {daily.correct}/{daily.total}</>
                : <><Sun className="ico" size={18} aria-hidden="true" /> {t("Thử thách hôm nay (5 câu)", "Today's challenge (5 questions)")}</>}
            </button>
          </div>
          {!diag && (
            <p className="small mt mb-0">
              <Gauge className="ico" size={16} aria-hidden="true" />{" "}
              {t("Chưa biết bắt đầu từ đâu?", "Not sure where to start?")}{" "}
              <Link to="/diagnostic">{t("Làm bài kiểm tra trình độ 12 câu", "Take the 12-question placement test")}</Link>
            </p>
          )}
        </div>
        <div className="stat-tiles">
          <div className="stat"><div className="value"><Flame className="flame" aria-hidden="true" /> {liveStreak(p.streak, today)}</div><div className="label">{t(`ngày liên tiếp · kỷ lục ${p.streak.best}`, `day streak · best ${p.streak.best}`)}</div></div>
          <div className="stat"><div className="value"><Star className="star" aria-hidden="true" /> {lvl.level}</div><div className="label">{t("cấp độ", "level")} · {p.xp} XP</div><ProgressBar value={(lvl.into / lvl.need) * 100} label={t("Tiến độ lên cấp", "Level progress")} /></div>
          <Link to="/review" className="stat card link flat"><div className="value"><RotateCcw aria-hidden="true" /> {due}</div><div className="label">{t("câu cần ôn hôm nay", "due for review today")}</div></Link>
          <div className="stat"><div className="value"><Brain aria-hidden="true" /> {mastered}</div><div className="label">{t(`câu đã thành thạo / ${bankStats().total}`, `mastered / ${bankStats().total}`)}</div></div>
        </div>
      </section>

      <div className="section-title">
        <h2>{t("Lộ trình chứng chỉ", "Certification path")}</h2>
        <Link to="/exams" className="small">{t("Xem chi tiết", "Details")} <ArrowRight className="ico" size={16} aria-hidden="true" /></Link>
      </div>
      <div className="roadmap">
        {EXAMS.map((e) => {
          const m = examMastery(e.id, p);
          const r = readiness(e.id, p);
          return (
            <Link key={e.id} to={`/exams/${e.id}`} className="card link">
              <div className="row"><ExamBadge examId={e.id} /><strong>{e.code}</strong></div>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{e.name.replace("Claude Certified ", "")}</div>
                <div className="small muted">{pick(e.tagline, lang)}</div>
              </div>
              <ProgressBar value={(m.seen / m.total) * 100} label={`${t("Đã luyện", "Practiced")} ${e.code}`} />
              <div className="row between tiny muted">
                <span>{m.seen}/{m.total} {t("câu", "questions")}</span>
                <span>{r === null ? t("Chưa đủ dữ liệu", "Not enough data") : `${t("Dự đoán", "Predicted")}: ${r}`}</span>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="section-title"><h2>{t("Học theo cách của bạn", "Learn your way")}</h2></div>
      <div className="grid grid-3">
        <Link to="/learn" className="card link">
          <span className="icon-tile"><BookOpen size={24} aria-hidden="true" /></span>
          <h3>{t("Lộ trình học", "Learning path")}</h3>
          <p className="small muted mb-0">{t(`${lessons.length} bài ngắn 8–15 phút, từ con số 0. Mỗi bài kèm câu kiểm tra và lab.`, `${lessons.length} short 8–15 minute lessons from zero, each with a quiz and lab.`)}</p>
        </Link>
        <Link to="/labs" className="card link">
          <span className="icon-tile ok"><FlaskConical size={24} aria-hidden="true" /></span>
          <h3>{t("Lab thực hành", "Hands-on labs")}</h3>
          <p className="small muted mb-0">{t("Viết prompt, thiết kế tool, sửa schema, tìm bug cấu hình — chấm điểm tự động, không cần API key.", "Write prompts, design tools, fix schemas, find config bugs — auto-graded, no API key needed.")}</p>
        </Link>
        <Link to="/exams" className="card link">
          <span className="icon-tile warn"><Timer size={24} aria-hidden="true" /></span>
          <h3>{t("Thi thử", "Mock exams")}</h3>
          <p className="small muted mb-0">{t("Bấm giờ theo nhịp đề thật, chia câu theo tỷ trọng domain chính thức, thang 100–1000.", "Timed at the real exam's pace, weighted by the official domain blueprint, scored 100–1000.")}</p>
        </Link>
        <Link to="/guide" className="card link">
          <span className="icon-tile blue"><Compass size={24} aria-hidden="true" /></span>
          <h3>{t("Cẩm nang thi", "Exam guide")}</h3>
          <p className="small muted mb-0">{t("Thể thức, lệ phí, thi lại, gia hạn, NDA, thuật ngữ và tài liệu chính thức — tổng hợp từ nguồn Anthropic.", "Format, fees, retakes, renewal, NDA, glossary and official resources — compiled from Anthropic sources.")}</p>
        </Link>
        <Link to="/review" className="card link">
          <span className="icon-tile purple"><RotateCcw size={24} aria-hidden="true" /></span>
          <h3>{t("Ôn tập thông minh", "Smart review")}</h3>
          <p className="small muted mb-0">{t("Câu sai quay lại đúng lúc bạn sắp quên (Leitner 5 hộp). Kèm flashcards cho con số cốt lõi.", "Missed questions return just before you forget (5-box Leitner), plus flashcards for key facts.")}</p>
        </Link>
        <Link to="/progress" className="card link">
          <span className="icon-tile"><Trophy size={24} aria-hidden="true" /></span>
          <h3>{t("Huy hiệu & tiến độ", "Badges & progress")}</h3>
          <p className="small muted mb-0">{t(`Lịch học, lịch sử bài thi và ${ACHIEVEMENTS.length} huy hiệu để chinh phục.`, `Activity calendar, exam history and ${ACHIEVEMENTS.length} badges to earn.`)}</p>
        </Link>
      </div>
      <p className="small muted mt"><Layers className="ico" size={16} aria-hidden="true" /> <Link to="/flashcards">Flashcards</Link> · <Link to="/glossary">{t("Thuật ngữ", "Glossary")}</Link> · <Link to="/resources">{t("Tài liệu chính thức", "Official resources")}</Link></p>
    </>
  );
}
